import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import "./budgets.css";

const emptyBudgetForm = () => ({
  name: "",
  description: "",
  budget_type: "annual",
  fiscal_year: new Date().getFullYear(),
  start_date: `${new Date().getFullYear()}-01-01`,
  end_date: `${new Date().getFullYear()}-12-31`,
  reporting_granularity: "monthly",
});

const BUDGET_TYPES = [
  "annual",
  "department",
  "project",
  "reforecast",
  "forecast",
  "other",
];

const GRANULARITIES = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
  { value: "custom", label: "Custom" },
];

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function statusClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "status-active";
  if (s === "archived") return "status-archived";
  return "status-draft";
}

function CreateBudgetModal({
  open,
  onClose,
  onCreated,
  companyId,
  sessionUserId,
}) {
  const [form, setForm] = useState(emptyBudgetForm());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setForm(emptyBudgetForm());
      setErr("");
      setSaving(false);
    }
  }, [open]);

  const canSubmit = useMemo(() => {
    return (
      companyId &&
      sessionUserId &&
      form.name.trim() &&
      form.fiscal_year &&
      form.start_date &&
      form.end_date
    );
  }, [companyId, sessionUserId, form]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    if (!canSubmit) {
      setErr("Please complete all required fields.");
      return;
    }

    try {
      setSaving(true);

      const { data: budgetRow, error: budgetErr } = await supabase
        .from("budgets")
        .insert({
          company_id: companyId,
          name: form.name.trim(),
          description: form.description.trim() || null,
          budget_type: form.budget_type,
          fiscal_year: Number(form.fiscal_year),
          start_date: form.start_date,
          end_date: form.end_date,
          input_granularity: "monthly",
          reporting_granularity: form.reporting_granularity,
          status: "draft",
          created_by: sessionUserId,
          updated_by: sessionUserId,
        })
        .select()
        .single();

      if (budgetErr) throw budgetErr;

      const { error: versionErr } = await supabase
        .from("budget_versions")
        .insert({
          company_id: companyId,
          budget_id: budgetRow.id,
          version_name: "Original Budget",
          version_code: "V1",
          version_type: "original",
          status: "draft",
          is_current: true,
          is_locked: false,
          created_by: sessionUserId,
          updated_by: sessionUserId,
        });

      if (versionErr) throw versionErr;

      await onCreated?.(budgetRow);
      onClose?.();
    } catch (error) {
      console.error("Create budget error:", error);
      setErr(error.message || "Failed to create budget.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="kp-modal-overlay" onClick={onClose}>
      <div
        className="kp-modal kp-modal-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="kp-modal-header">
          <div>
            <h3>Create Budget</h3>
            <p>Set up a new company budget and its initial version.</p>
          </div>
          <button
            type="button"
            className="kp-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="kp-form">
          <div className="kp-form-grid">
            <div className="kp-field">
              <label>Budget Name</label>
              <input
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="e.g. 2026 Annual Budget"
                required
              />
            </div>

            <div className="kp-field">
              <label>Budget Type</label>
              <select
                value={form.budget_type}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    budget_type: e.target.value,
                  }))
                }
              >
                {BUDGET_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="kp-field">
              <label>Fiscal Year</label>
              <input
                type="number"
                value={form.fiscal_year}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    fiscal_year: e.target.value,
                  }))
                }
                required
              />
            </div>

            <div className="kp-field">
              <label>Reporting View</label>
              <select
                value={form.reporting_granularity}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    reporting_granularity: e.target.value,
                  }))
                }
              >
                {GRANULARITIES.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="kp-field">
              <label>Start Date</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    start_date: e.target.value,
                  }))
                }
                required
              />
            </div>

            <div className="kp-field">
              <label>End Date</label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    end_date: e.target.value,
                  }))
                }
                required
              />
            </div>
          </div>

          <div className="kp-field">
            <label>Description</label>
            <textarea
              rows="3"
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Optional notes about this budget"
            />
          </div>

          {err && <div className="kp-alert kp-alert-error">{err}</div>}

          <div className="kp-modal-footer">
            <button
              type="button"
              className="kp-btn kp-btn-soft"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="kp-btn kp-btn-primary"
              disabled={saving || !canSubmit}
            >
              {saving ? "Creating..." : "Create Budget"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Budgets() {
  const navigate = useNavigate();
  const { companyId } = useCompany();

  const [sessionUserId, setSessionUserId] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState("");
  const [budgets, setBudgets] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [filters, setFilters] = useState({
    query: "",
    status: "all",
    year: "all",
  });

  const loadSessionAndRole = useCallback(async () => {
    if (!companyId) return;

    const {
      data: { user },
      error: authErr,
    } = await supabase.auth.getUser();

    if (authErr) throw authErr;
    if (!user?.id) throw new Error("User session not found.");

    setSessionUserId(user.id);

    const { data: member, error: memberErr } = await supabase
      .from("company_members")
      .select("role, accepted")
      .eq("company_id", companyId)
      .eq("user_id", user.id)
      .maybeSingle();

    if (memberErr) throw memberErr;

    setRole(String(member?.role || "member").toLowerCase());
  }, [companyId]);

  const loadBudgets = useCallback(async () => {
    if (!companyId) return;

    const { data, error } = await supabase
      .from("budgets")
      .select("*")
      .eq("company_id", companyId)
      .order("created_at", { ascending: false });

    if (error) throw error;

    setBudgets(data || []);
  }, [companyId]);

  const initialize = useCallback(async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      setErr("");
      await Promise.all([loadSessionAndRole(), loadBudgets()]);
    } catch (error) {
      console.error("Budgets page load error:", error);
      setErr(error.message || "Failed to load budgets.");
    } finally {
      setLoading(false);
    }
  }, [companyId, loadBudgets, loadSessionAndRole]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setErr("");
      await loadBudgets();
    } catch (error) {
      console.error("Budget refresh error:", error);
      setErr(error.message || "Failed to refresh budgets.");
    } finally {
      setRefreshing(false);
    }
  };

  const handleCreated = async (budgetRow) => {
    await loadBudgets();
    if (budgetRow?.id) {
      navigate(`/budget/${budgetRow.id}`);
    }
  };

  const years = useMemo(() => {
    return Array.from(
      new Set((budgets || []).map((b) => String(b.fiscal_year || "")))
    )
      .filter(Boolean)
      .sort((a, b) => Number(b) - Number(a));
  }, [budgets]);

  const filteredBudgets = useMemo(() => {
    const query = filters.query.trim().toLowerCase();

    return (budgets || []).filter((b) => {
      const matchesQuery =
        !query ||
        String(b.name || "")
          .toLowerCase()
          .includes(query) ||
        String(b.description || "")
          .toLowerCase()
          .includes(query) ||
        String(b.budget_type || "")
          .toLowerCase()
          .includes(query);

      const matchesStatus =
        filters.status === "all" ||
        String(b.status || "").toLowerCase() === filters.status;

      const matchesYear =
        filters.year === "all" || String(b.fiscal_year || "") === filters.year;

      return matchesQuery && matchesStatus && matchesYear;
    });
  }, [budgets, filters]);

  if (loading) {
    return (
      <div className="kp-budget-page">
        <div className="kp-budget-loading">Loading budgets…</div>
      </div>
    );
  }

  return (
    <div className="kp-budget-page">
      <div className="kp-budget-topbar">
        <div>
          <h2>Budgets</h2>
          <p>
            View all company budgets and open any budget workspace. Role:{" "}
            <strong>{role || "member"}</strong>
          </p>
        </div>

        <div className="kp-inline-actions">
          <button
            type="button"
            className="kp-btn kp-btn-soft"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>

          <button
            type="button"
            className="kp-btn kp-btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            Create Budget
          </button>
          <button
            type="button"
            className="kp-btn kp-btn-soft"
            onClick={() => navigate("/forecast-builder")}
          >
            Forecast Builder
          </button>
          <button
            type="button"
            className="kp-btn kp-btn-soft"
            onClick={() => navigate("/driver-library")}
          >
            Driver Library
          </button>
          <button
            type="button"
            className="kp-btn kp-btn-soft"
            onClick={() => navigate("/formula-manager")}
          >
            Formula Manager
          </button>
          <button
            type="button"
            className="kp-btn kp-btn-soft"
            onClick={() => navigate("/scenario-manager")}
          >
            Scenario Manager
          </button>
        </div>
      </div>

      {err && <div className="kp-alert kp-alert-error">{err}</div>}

      <div className="kp-card">
        <div className="kp-card-head">
          <h3>Budget List</h3>
        </div>

        <div className="kp-form-grid kp-form-grid-3 kp-report-filters">
          <div className="kp-field">
            <label>Search</label>
            <input
              placeholder="Search by name, description or type"
              value={filters.query}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, query: e.target.value }))
              }
            />
          </div>

          <div className="kp-field">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, status: e.target.value }))
              }
            >
              <option value="all">All</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          <div className="kp-field">
            <label>Fiscal Year</label>
            <select
              value={filters.year}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, year: e.target.value }))
              }
            >
              <option value="all">All</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="kp-table-wrap">
          <table className="kp-table">
            <thead>
              <tr>
                <th>Budget Name</th>
                <th>Type</th>
                <th>Fiscal Year</th>
                <th>Period</th>
                <th>Status</th>
                <th>Created</th>
                <th>Last Updated</th>
              </tr>
            </thead>

            <tbody>
              {filteredBudgets.length === 0 ? (
                <tr>
                  <td colSpan="7" className="kp-empty-cell">
                    No budgets found.
                  </td>
                </tr>
              ) : (
                filteredBudgets.map((budget) => (
                  <tr
                    key={budget.id}
                    className="kp-clickable-row"
                    onClick={() => navigate(`/budget/${budget.id}`)}
                    title="Open budget workspace"
                  >
                    <td>
                      <div className="kp-line-main">
                        <strong>{budget.name}</strong>
                        {budget.description ? (
                          <small>{budget.description}</small>
                        ) : null}
                      </div>
                    </td>
                    <td>{budget.budget_type || "—"}</td>
                    <td>{budget.fiscal_year || "—"}</td>
                    <td>
                      {formatDate(budget.start_date)} –{" "}
                      {formatDate(budget.end_date)}
                    </td>
                    <td>
                      <span
                        className={`kp-badge ${statusClass(budget.status)}`}
                      >
                        {budget.status || "draft"}
                      </span>
                    </td>
                    <td>{formatDateTime(budget.created_at)}</td>
                    <td>{formatDateTime(budget.updated_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CreateBudgetModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleCreated}
        companyId={companyId}
        sessionUserId={sessionUserId}
      />
    </div>
  );
}
